import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Popper, Portal, Unstable_TrapFocus as FocusTrap, alpha, useTheme } from '@mui/material'
import { advancedColors } from '../theme'
import { TourStepDefinition } from './steps'
import TourCard from './TourCard'
import { useTourTarget, useTrackedRect } from './useTourTarget'
import { useSetTourExample, useSetTourRunning } from './tourExample'

const SPOTLIGHT_PADDING = 6
const CARD_OFFSET = 16
const VIEWPORT_PADDING = 16
const ARROW_SIZE = 14
const ARROW_PADDING = 16
const POPPER_OPTIONS = { strategy: 'fixed' as const }

const arrowSx = {
  position: 'absolute',
  width: ARROW_SIZE,
  height: ARROW_SIZE,
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    backgroundColor: advancedColors.surface,
    transform: 'rotate(45deg)',
  },
}

const popperSx = (zIndex: number) => ({
  zIndex,
  '&[data-popper-placement^="top"] .TourArrow': { bottom: -ARROW_SIZE / 2 },
  '&[data-popper-placement^="bottom"] .TourArrow': { top: -ARROW_SIZE / 2 },
  '&[data-popper-placement^="left"] .TourArrow': { right: -ARROW_SIZE / 2 },
  '&[data-popper-placement^="right"] .TourArrow': { left: -ARROW_SIZE / 2 },
})

type Hole = { top: number; left: number; width: number; height: number }

const holeAround = (rect: DOMRect): Hole => ({
  top: rect.top - SPOTLIGHT_PADDING,
  left: rect.left - SPOTLIGHT_PADDING,
  width: rect.width + SPOTLIGHT_PADDING * 2,
  height: rect.height + SPOTLIGHT_PADDING * 2,
})

// Box-shadows take no clicks, so these block the dimmed area.
const blockersAround = ({ top, left, width, height }: Hole) => ({
  above: { top: 0, left: 0, right: 0, height: Math.max(top, 0) },
  below: { top: top + height, left: 0, right: 0, bottom: 0 },
  before: { top, left: 0, width: Math.max(left, 0), height },
  after: { top, left: left + width, right: 0, height },
})

// An open menu traps focus itself, so this trap stands down until it closes.
const isTrapEnabled = (): boolean => !document.querySelector('.MuiMenu-root')

type Props = {
  steps: TourStepDefinition[]
  openTab: (tabId: string) => void
  onEnd: () => void
  setCareTeamChatOpen?: (open: boolean) => void
}

const TourRunner = ({ steps, openTab, onEnd, setCareTeamChatOpen }: Props) => {
  const theme = useTheme()
  const [index, setIndex] = useState(0)
  const direction = useRef<1 | -1>(1)
  const popper = useRef<{ update: () => unknown } | null>(null)
  const root = useRef<HTMLDivElement | null>(null)
  const [arrow, setArrow] = useState<HTMLElement | null>(null)
  const step = steps[index]
  const setTourExample = useSetTourExample()
  const setTourRunning = useSetTourRunning()
  const { ready, element, missing, relocate } = useTourTarget(step, openTab)
  const rect = useTrackedRect(element, relocate)

  const go = useCallback(
    (delta: 1 | -1) => {
      direction.current = delta
      const next = index + delta
      if (next >= steps.length) {
        onEnd()
      } else {
        setIndex(Math.max(next, 0))
      }
    },
    [index, steps.length, onEnd]
  )

  useEffect(() => {
    if (missing) {
      go(direction.current)
    }
  }, [missing, go])

  useEffect(() => {
    if (step.opensCareTeamChat === undefined || !setCareTeamChatOpen) {
      return undefined
    }
    setCareTeamChatOpen(step.opensCareTeamChat)

    return () => setCareTeamChatOpen(false)
  }, [step, setCareTeamChatOpen])

  useEffect(() => {
    setTourRunning(true)

    return () => setTourRunning(false)
  }, [setTourRunning])

  useEffect(() => {
    setTourExample(step.example ?? null)

    return () => setTourExample(null)
  }, [step, setTourExample])

  useEffect(() => {
    popper.current?.update()
  }, [rect])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEnd()
      }
    }
    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onEnd])

  useEffect(() => {
    // A menu's clicks always get through, so a stray one can close; an `opensMenu` step also passes its target's.
    const onClick = (event: MouseEvent) => {
      const { target } = event
      if (!(target instanceof Node) || root.current?.contains(target)) {
        return
      }
      if (target instanceof Element) {
        if (target.closest('.MuiMenu-root') || (step.opensMenu && element?.contains(target))) {
          return
        }
      }
      event.preventDefault()
      event.stopPropagation()
    }
    document.addEventListener('click', onClick, true)

    return () => document.removeEventListener('click', onClick, true)
  }, [step.opensMenu, element])

  const setPopper = useCallback((instance: { update: () => unknown } | null) => {
    popper.current = instance
  }, [])

  const modifiers = useMemo(
    () => [
      { name: 'offset', options: { offset: [0, SPOTLIGHT_PADDING + CARD_OFFSET] } },
      { name: 'flip', options: { padding: VIEWPORT_PADDING } },
      { name: 'preventOverflow', options: { padding: VIEWPORT_PADDING } },
      { name: 'arrow', options: { element: arrow, padding: ARROW_PADDING } },
    ],
    [arrow]
  )

  const zIndex = theme.zIndex.modal
  const dim = alpha(advancedColors.deep, 0.72)
  const hole = rect ? holeAround(rect) : null
  const card = (
    <TourCard
      key={step.id}
      step={step}
      index={index}
      size={steps.length}
      onBack={() => go(-1)}
      onNext={() => go(1)}
      onClose={onEnd}
    />
  )

  return (
    <Portal>
      <FocusTrap open isEnabled={isTrapEnabled}>
        <Box ref={root} tabIndex={-1} sx={{ outline: 'none' }}>
          {hole ? (
            <>
              <Box
                aria-hidden
                style={hole}
                sx={{
                  position: 'fixed',
                  zIndex,
                  borderRadius: '12px',
                  boxShadow: `0 0 0 100vmax ${dim}`,
                  pointerEvents: step.interactive ? 'none' : 'auto',
                  transition: theme.transitions.create(['top', 'left', 'width', 'height'], { duration: 150 }),
                }}
              />
              {Object.entries(blockersAround(hole)).map(([side, area]) => (
                <Box key={side} aria-hidden style={area} sx={{ position: 'fixed', zIndex }} />
              ))}
            </>
          ) : (
            <Box aria-hidden sx={{ position: 'fixed', inset: 0, zIndex, backgroundColor: dim }} />
          )}

          {ready && !step.selector && (
            <Box
              sx={{
                position: 'fixed',
                zIndex: zIndex + 1,
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              {card}
            </Box>
          )}
          {element && (
            <Popper
              open
              disablePortal
              anchorEl={element}
              placement={step.placement ?? 'auto'}
              popperRef={setPopper}
              popperOptions={POPPER_OPTIONS}
              modifiers={modifiers}
              sx={popperSx(zIndex + 1)}
            >
              <Box ref={setArrow} className="TourArrow" sx={arrowSx} />
              {card}
            </Popper>
          )}
        </Box>
      </FocusTrap>
    </Portal>
  )
}

export default TourRunner
